/**
 * v4.30 — PortalBrandMark.
 *
 * The single component that paints organization identity into every
 * shell surface. Used by:
 *   - Sidebar.jsx top-left header block  (variant="sidebar")
 *   - Layout.jsx top command bar         (variant="topbar")
 *   - Operational Base shell (future absorption of OperationalBrandHeader)
 *
 * Calm contract:
 *   - Reserves exact final dimensions before fetch resolves → zero layout jump.
 *   - Picks light-vs-dark logo by reading the live `:root.dark` class state
 *     so a theme toggle does not require a refetch.
 *   - Transparent-friendly: never paints a white box behind the logo.
 *   - Size normalized: fixed max-height + object-fit:contain prevents stretch.
 *   - Graceful YulOnTap recession: when org branding exists, the platform
 *     mark recedes to a small footnote (rendered separately in the parent
 *     shell); when no org branding, this component falls back to the
 *     YulOnTap mark naturally.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranding } from '../../lib/branding';

const _detectDark = () => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
};

/** Subscribe to `:root.dark` class toggles via a MutationObserver. */
const useIsDarkTheme = () => {
    const [dark, setDark] = useState(_detectDark);
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const root = document.documentElement;
        const obs = new MutationObserver(() => setDark(_detectDark()));
        obs.observe(root, { attributes: true, attributeFilter: ['class'] });
        return () => obs.disconnect();
    }, []);
    return dark;
};

/* Variant → fixed dimensions. These are the *reserved* dimensions —
 * matching the rendered logo box exactly so no layout jump occurs
 * while branding loads or when a logo is missing. */
const _DIMENSIONS = {
    /* Top-left of the sidebar — replaces the legacy YulOnTap block. */
    sidebar: { maxH: 40, maxW: 152, gap: 12, labelSize: '0.95rem', taglineSize: '0.62rem' },
    /* Calm small mark in the top command bar — left of InsightToggle. */
    topbar: { maxH: 24, maxW: 96, gap: 8, labelSize: '0.82rem', taglineSize: '0.58rem' },
    /* Reserved future variants. */
    inline: { maxH: 28, maxW: 100, gap: 10, labelSize: '0.78rem', taglineSize: '0.62rem' },
};

export const PortalBrandMark = ({
    variant = 'sidebar',
    showLabel = true,
    /* If true, when there is NO org branding, render the YulOnTap
     * platform fallback. Sidebar passes true (always paint something);
     * topbar passes false (stay empty on platform-default tenants —
     * avoids double YulOnTap presence with the sidebar mark). */
    fallbackToPlatform = false,
    /* Optional explicit payload — bypasses useBranding() entirely.
     * Used by the in-modal ShellPreviewCard so the mini-shell can
     * render the uploaded logos WITHOUT hijacking every other surface
     * on the page. Shape matches the /branding response. */
    override = null,
    className = '',
    'data-testid': testId,
}) => {
    const { t } = useTranslation();
    const dim = _DIMENSIONS[variant] || _DIMENSIONS.sidebar;
    const hookResult = useBranding();
    const isDark = useIsDarkTheme();
    const loaded = override ? true : hookResult.loaded;
    const payload = override || hookResult.payload;

    /* Reserve space before the first fetch resolves. */
    if (!loaded) {
        return (
            <div
                aria-hidden="true"
                data-testid={testId || `portal-brand-mark-${variant}-loading`}
                style={{ height: dim.maxH, minWidth: dim.maxH, opacity: 0 }}
            />
        );
    }

    const hasOrgIdentity = !!(
        payload && (
            (payload.logo_source && payload.logo_source !== 'platform') ||
            /* Override path: when an explicit payload is passed in we
             * treat any non-empty logo URL as an org identity, since
             * the parent (mini-shell preview, live preview override)
             * already knows the source is org. */
            (override && (payload.logo_url || payload.logo_dark_url))
        )
    );
    const logoUrl = isDark
        ? (payload?.logo_dark_url || payload?.logo_url || null)
        : (payload?.logo_url || payload?.logo_dark_url || null);
    const orgLabel = payload?.organization?.display_name
        || payload?.brand_name
        || payload?.organization?.name
        || null;

    /* Platform fallback (YulOnTap). Only emit when caller opts in
     * (sidebar) — topbar stays empty to avoid duplication. */
    if (!hasOrgIdentity) {
        if (!fallbackToPlatform) {
            /* Still reserve space so the layout doesn't shift later. */
            return (
                <div
                    aria-hidden="true"
                    data-testid={testId || `portal-brand-mark-${variant}-empty`}
                    style={{ height: dim.maxH, minWidth: dim.maxH }}
                />
            );
        }
        return (
            <div
                className={`inline-flex items-center ${className}`}
                style={{ gap: dim.gap }}
                data-testid={testId || `portal-brand-mark-${variant}`}
                data-source="platform"
            >
                <img
                    src="/yulontap-logo.png"
                    alt="YulOnTap"
                    style={{
                        height: dim.maxH,
                        width: dim.maxH,
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />
                {showLabel && (
                    <div className="flex flex-col" style={{ minWidth: 0 }}>
                        <span
                            className="font-bold text-slate-800 dark:text-slate-100"
                            style={{ fontSize: dim.labelSize, lineHeight: 1.15 }}
                        >
                            YulOnTap
                        </span>
                        <span
                            className="uppercase text-slate-500 dark:text-slate-400"
                            style={{
                                fontSize: dim.taglineSize,
                                letterSpacing: '0.18em',
                                lineHeight: 1.2,
                            }}
                        >
                            {t('portalIdentity.sidebar.platformTagline')}
                        </span>
                    </div>
                )}
            </div>
        );
    }

    /* Org/brand identity present. */
    return (
        <div
            className={`inline-flex items-center ${className}`}
            style={{ gap: dim.gap }}
            data-testid={testId || `portal-brand-mark-${variant}`}
            data-source={payload?.logo_source || 'org'}
        >
            {logoUrl && (
                <img
                    src={logoUrl}
                    alt={orgLabel || 'Organization'}
                    /* Never paint a backing color — transparent assets stay transparent. */
                    style={{
                        height: dim.maxH,
                        maxWidth: dim.maxW,
                        width: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                        /* Browsers occasionally render transparent PNGs with
                         * a faint background unless we explicitly say none. */
                        background: 'transparent',
                    }}
                    onError={(e) => {
                        /* If the dark variant 404s, fall through to light. */
                        if (isDark && payload?.logo_url && e.currentTarget.src !== payload.logo_url) {
                            e.currentTarget.src = payload.logo_url;
                        }
                    }}
                />
            )}
            {showLabel && orgLabel && (
                <div className="flex flex-col" style={{ minWidth: 0 }}>
                    {/*
                      v4.33.2 — Org names longer than ~17 chars (e.g.
                      "Le Balthazar Boisbriand") were truncated with an
                      ellipsis because we capped them at one line. We
                      now allow the label to wrap to TWO lines while
                      preserving the reserved width. The fixed line
                      height keeps the header height stable enough that
                      the rest of the sidebar layout never jumps.
                    */}
                    <span
                        className="font-bold text-slate-800 dark:text-slate-100"
                        style={{
                            fontSize: dim.labelSize,
                            lineHeight: 1.15,
                            maxWidth: dim.maxW,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                        }}
                        title={orgLabel}
                    >
                        {orgLabel}
                    </span>
                    <span
                        className="uppercase text-slate-500 dark:text-slate-400"
                        style={{
                            fontSize: dim.taglineSize,
                            letterSpacing: '0.18em',
                            lineHeight: 1.2,
                        }}
                    >
                        {t('portalIdentity.sidebar.operationsEyebrow')}
                    </span>
                </div>
            )}
        </div>
    );
};

export default PortalBrandMark;
